import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles } from 'lucide-react';
import { Screen } from '@/app/layout/Screen';
import { Button } from '@/components/ui/Button';
import { LoadFailure, PanelSkeleton, StateView } from '@/components/ui/Feedback';
import { Lead, MiniIcon, PageTitle, Panel, Row, RowList } from '@/components/ui/Surface';
import { useAiToolsStore } from '@/store/aiToolsStore';

/**
 * Вкладка «ИИ»: инструменты ассистента.
 *
 * Список приходит с `/api/miniapp/smm-tools` — это не часть программы курса.
 * Работает инструмент через тот же диалог по `assignment_id`, поэтому открытие
 * ведёт на обычный экран задания с пометкой, что вернуться нужно во вкладку ИИ.
 */
export function AiToolsScreen() {
  const navigate = useNavigate();

  const tools = useAiToolsStore((state) => state.tools);
  const loading = useAiToolsStore((state) => state.loading);
  const error = useAiToolsStore((state) => state.error);
  const load = useAiToolsStore((state) => state.load);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading && tools.length === 0) {
    return (
      <Screen subtitle="ИИ-ассистент" tab="ai">
        <PanelSkeleton count={4} />
      </Screen>
    );
  }

  if (error && tools.length === 0) {
    return (
      <Screen subtitle="ИИ-ассистент" tab="ai">
        <LoadFailure title="Инструменты не загрузились" description={error} onRetry={() => void load()} />
      </Screen>
    );
  }

  return (
    <Screen subtitle="ИИ-ассистент" tab="ai">
      <Panel tone="brand" className="mb-3">
        <div className="flex items-start gap-2.5">
          <MiniIcon tone="soft">
            <Sparkles className="h-[17px] w-[17px]" aria-hidden />
          </MiniIcon>
          <div>
            <h2 className="text-[16px] font-medium text-white">Что создадим сегодня?</h2>
            <p className="mt-1 text-[12px] leading-[1.45] text-white/80">
              Ассистент опирается на вашу анкету и материалы пройденных уроков.
            </p>
          </div>
        </div>
      </Panel>

      {tools.length === 0 ? (
        <StateView
          icon={<Sparkles className="h-6 w-6" aria-hidden />}
          title="Инструменты пока не открыты"
          description="Они появятся, когда станут доступны в вашем курсе."
          primary={
            <Button variant="primary" block onClick={() => navigate('/course')}>
              Открыть программу
            </Button>
          }
        />
      ) : (
        <>
          <PageTitle className="mt-1">Инструменты</PageTitle>
          <Lead>Каждый инструмент — отдельный диалог с ассистентом и готовый материал.</Lead>

          <RowList>
            {tools.map((tool) => (
              <div key={tool.assignment_id}>
                <Row
                  icon={
                    <MiniIcon tone="lime">
                      <Sparkles className="h-[17px] w-[17px]" aria-hidden />
                    </MiniIcon>
                  }
                  title={tool.title}
                  chevron
                  onClick={() => navigate(`/course/lessons/${tool.assignment_id}?from=ai`)}
                />
              </div>
            ))}
          </RowList>
        </>
      )}
    </Screen>
  );
}
